import {
  effectiveBringerForBooking,
  normalizeSpotKey,
  resolveBringerPlaceholders,
  resolveSpotPlaceholders,
  venueHasSpotLengths,
} from './bookingSpot.js';

/** The shared offer template still says "for a 5 minute spot". Participation nights drop that. */
export function withoutFixedFiveMinuteSpot(body) {
  return String(body || '').replace(/,\s*for a 5 minute spot\.?/gi, '.');
}

/**
 * @see docs/APP_UI_SPEC.md — B1 (template placeholders; keep in sync with send-application-email edge function)
 *
 * Booking Email Utilities
 *
 * Generates booking confirmation emails with the same format used in
 * the send-application-email edge function.
 *
 * This ensures consistency between:
 * 1. The mailto links in the admin notification emails
 * 2. The email buttons in the AdminDatesPage UI
 */

/**
 * Generate the email body by replacing template placeholders with actual values.
 * Mirrors the logic in supabase/functions/send-application-email/index.ts
 *
 * @param {string} templateBody - The email template with {{placeholders}}
 * @param {object} data - The data to fill in the template
 * @param {string} data.comedianName - The comedian's full name (first name will be used)
 * @param {string} data.eventName - The event name/date display string
 * @param {string} data.venueName - The venue name
 * @param {string} data.venueLocation - The venue address
 * @param {string} data.googleMapLink - The Google Maps URL
 * @param {string} data.doorsTime - The doors/check-in time (default: '7:30 PM')
 * @param {string} data.showTime - The show start time (default: '8:00 PM')
 * @param {string} data.showEndTime - The show end time (default: '10:00 PM')
 * @param {string} [data.spotCode] - '5' | '7' | '10' | 'headliner' (legacy `H`) — fills {{spot_*}}
 * @param {boolean} [data.isBringer] - bringer only applies to **5**-minute spots; ignored for 7 / 10 / headliner
 * @returns {string} The processed email body
 */
export function generateBookingEmailBody(templateBody, data) {
  const {
    comedianName = 'Funny Person!',
    eventName = '',
    venueName = 'Comedy Night',
    venueLocation = '',
    googleMapLink = '',
    doorsTime = '7:30 PM',
    showTime = '8:00 PM',
    showEndTime = '10:00 PM',
    spotCode,
    isBringer: isBringerRaw,
    participationOnly = false,
  } = data;

  const spot = resolveSpotPlaceholders(participationOnly ? '' : spotCode);
  const canon = normalizeSpotKey(participationOnly ? '' : spotCode);
  const effectiveBringer = participationOnly
    ? false
    : effectiveBringerForBooking(spotCode, isBringerRaw);
  const bri = participationOnly
    ? { is_bringer: '', bringer_line: '', bringer_short: '', bringer_note: '' }
    : resolveBringerPlaceholders(effectiveBringer);
  const spotLabelFull =
    spot.spot_label_short &&
    (canon === 'headliner' ? spot.spot_label_short : `${spot.spot_label_short}${bri.bringer_short}`);

  // Extract first name from full name
  const firstName = comedianName?.split(' ')[0] || 'Funny Person!';

  let emailBody = templateBody
    // Replace comedian name with first name only
    .replace(/{{comedian_name}}/g, firstName)
    // Replace event date with event name
    .replace(/{{event_date}}/g, eventName)
    // Replace venue name
    .replace(/{{venue_name}}/g, venueName)
    // Replace venue location/address
    .replace(/{{venue_location}}/g, venueLocation)
    // Replace Google Maps URL
    .replace(/{{google_map_link}}/g, googleMapLink)
    // Replace timing placeholders
    .replace(/{{doors_time}}/g, doorsTime)
    .replace(/{{show_time}}/g, showTime)
    .replace(/{{show_end_time}}/g, showEndTime)
    // Spot type (booking offer — B1); safe if template omits these
    .replace(/{{spot_code}}/g, spot.spot_code)
    .replace(/{{spot_label_short}}/g, spot.spot_label_short)
    .replace(/{{spot_description}}/g, spot.spot_description)
    .replace(/{{spot_note}}/g, spot.spot_note)
    .replace(/{{spot_label_full}}/g, spotLabelFull || '')
    .replace(/{{is_bringer}}/g, bri.is_bringer)
    .replace(/{{bringer_line}}/g, bri.bringer_line)
    .replace(/{{bringer_short}}/g, bri.bringer_short)
    .replace(/{{bringer_note}}/g, bri.bringer_note)
    // Also replace hardcoded "Sunset Comedy" references (for legacy templates)
    .replace(/Sunset Comedy/g, venueName);

  if (participationOnly) emailBody = withoutFixedFiveMinuteSpot(emailBody);

  // Convert escaped newlines to actual newlines
  emailBody = emailBody.replace(/\\n/g, '\n');

  return emailBody;
}

/**
 * Club mailbox for booking offers and reply routing (Half-claw / inbox pipeline).
 * Keep in sync with send-application-email and BOOKING_REPLY_IGNORE_SENDER_EMAILS.
 */
export const SUNSET_BOOKING_MAILBOX = 'sunsetcomedylondon@gmail.com';

/**
 * Build the booking-offer draft (to / subject / body) used by mailto and in-app Gmail send.
 * @see docs/APP_UI_SPEC.md — B1, B4
 *
 * @param {{ comedian_name: string, email: string }} application
 * @param {{ event_name: string, event_date: string, venues?: object }} event
 * @param {{ body: string }|null} emailTemplate
 * @param {{ spotCode?: string, isBringer?: boolean }} [options]
 * @returns {{ to: string, subject: string, body: string, replyTo: string }}
 */
export function composeBookingOffer(application, event, emailTemplate, options = {}) {
  const { spotCode, isBringer: isBringerOpt } = options;
  const venueName = event.venues?.venue_name || 'Comedy Night';
  const venueAddress = event.venues?.address || '';
  const googleMapUrl = event.venues?.google_maps_url || '';
  const doorsTime = event.venues?.doors_time || '7:30 PM';
  const showTime = event.venues?.show_time || '8:00 PM';
  const showEndTime = event.venues?.show_end_time || '10:00 PM';
  const subject = generateBookingSubject(event.event_date, venueName);

  const participationOnly = !venueHasSpotLengths(event.venues);
  if (!emailTemplate) {
    const firstName = application.comedian_name?.split(' ')[0] || 'there';
    const spot = resolveSpotPlaceholders(participationOnly ? '' : spotCode);
    const eb = effectiveBringerForBooking(spotCode, isBringerOpt);
    const bri = resolveBringerPlaceholders(eb);
    let body = `Hi ${firstName},\n\nCongratulations! You have been selected to perform.`;
    if (spot.spot_description) {
      body += `\n\nSpot: ${spot.spot_label_short}${bri.bringer_short} (${spot.spot_description}).\n${spot.spot_note}`;
      if (eb) body += `\n${bri.bringer_line}`;
    }
    return { to: application.email, subject, body, replyTo: SUNSET_BOOKING_MAILBOX };
  }

  const body = generateBookingEmailBody(emailTemplate.body, {
    comedianName: application.comedian_name,
    eventName: event.event_name,
    venueName,
    venueLocation: venueAddress,
    googleMapLink: googleMapUrl,
    doorsTime,
    showTime,
    showEndTime,
    spotCode: participationOnly ? '' : spotCode,
    isBringer: participationOnly ? false : isBringerOpt,
    participationOnly,
  });

  return { to: application.email, subject, body, replyTo: SUNSET_BOOKING_MAILBOX };
}

/**
 * Generate a mailto link with properly encoded subject and body.
 * Sets reply-to (RFC 6068 hname) so replies route to the club mailbox even when
 * a booker composes from a personal Gmail account.
 *
 * @param {object} options - The email options
 * @param {string} options.email - The recipient email address
 * @param {string} options.subject - The email subject
 * @param {string} options.body - The email body
 * @param {string|null} [options.replyTo=SUNSET_BOOKING_MAILBOX] - Reply-To header; pass null to omit
 * @returns {string} The complete mailto: URL
 */
export function generateMailtoLink({ email, subject, body, replyTo = SUNSET_BOOKING_MAILBOX }) {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);
  const params = [];
  if (replyTo) {
    const encoded = encodeURIComponent(replyTo);
    // RFC 6068 hname; Thunderbird expects canonical header casing (see Mozilla bug 1800759).
    // Reply-To first so dev console / copied URLs are easy to scan.
    params.push(`Reply-To=${encoded}`, `reply-to=${encoded}`);
  }
  params.push(`subject=${encodedSubject}`, `body=${encodedBody}`);

  return `mailto:${email}?${params.join('&')}`;
}

/**
 * Navigate to a booking-offer mailto URL (📧 buttons use this instead of <a href>).
 * In dev, logs the full URL to the console so you can copy/paste into Gmail for testing.
 */
export function openBookingMailtoLink(mailtoLink) {
  if (process.env.NODE_ENV !== 'production') {
    console.info('[booking mailto]', mailtoLink);
  }
  window.location.href = mailtoLink;
}

/**
 * Generate a complete booking email subject line.
 * Format: "You Are Booked for {eventDate} {venueName}!"
 *
 * @param {string} eventDate - The event date string
 * @param {string} venueName - The venue name
 * @returns {string} The formatted subject line
 */
export function generateBookingSubject(eventDate, venueName) {
  return `You Are Booked for ${eventDate} ${venueName}!`;
}

/**
 * PostgREST .ilike() pattern for replies to booking-offer threads (subject line after "Re:").
 * Keep in sync with generateBookingSubject() and isBookingReplySubject() in
 * supabase/functions/_shared/email-classify.ts (Gmail search uses subject:(You Are Booked)).
 */
export const BOOKING_REPLY_SUBJECT_ILIKE = 'Re: You Are Booked%';

/** First three letters of English month name → 0–11 (UTC month index). */
const MONTH_PREFIX_TO_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function utcYmdFromParts(year, monthIndex, day) {
  const d = new Date(Date.UTC(year, monthIndex, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== monthIndex ||
    d.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Parse event_date (YYYY-MM-DD) from our booking-offer subject.
 * Matches generateBookingSubject(event.event_date, venueName) → "You Are Booked for {date} {venue}!".
 *
 * @param {string} subject
 * @returns {string|null}
 */
export function parseOfferEventDateFromSubject(subject) {
  if (!subject || typeof subject !== 'string') return null;
  const m = subject.match(/You Are Booked for\s+(\d{4}-\d{2}-\d{2})\s+/i);
  return m ? m[1] : null;
}

/**
 * Fallback when the subject was mangled: same ISO pattern in the quoted thread, or a verbal
 * date like "Apr 27th 2026" from the booking body.
 *
 * @param {string} body
 * @returns {string|null} YYYY-MM-DD
 */
export function parseOfferEventDateFromBookingBody(body) {
  if (!body || typeof body !== 'string') return null;
  const slice = body.slice(0, 4000);
  const isoInBody = slice.match(/You Are Booked for\s+(\d{4}-\d{2}-\d{2})\s+/i);
  if (isoInBody) return isoInBody[1];

  const verbal = slice.match(
    /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\b/i
  );
  if (!verbal) return null;
  const mon = verbal[1].toLowerCase();
  const pref = mon.slice(0, 3);
  const monthIndex = MONTH_PREFIX_TO_INDEX[pref];
  if (monthIndex === undefined) return null;
  const day = parseInt(verbal[2], 10);
  const year = parseInt(verbal[3], 10);
  if (day < 1 || day > 31) return null;
  return utcYmdFromParts(year, monthIndex, day);
}

/**
 * Prefer subject (reply thread title), then body snippet (quoted offer).
 *
 * @param {{ subject?: string, body_snippet?: string }} row
 * @returns {string|null} YYYY-MM-DD
 */
export function parseOfferEventDateFromInboxRow(row) {
  const fromSubject = parseOfferEventDateFromSubject(row?.subject || '');
  if (fromSubject) return fromSubject;
  return parseOfferEventDateFromBookingBody(row?.body_snippet || '');
}

/**
 * Date used for inbox candidate matching and auto-confirm gate.
 * Prefers LLM `llm_inferred_event_date` when set (thread-agreed date may differ from subject).
 *
 * @param {{ subject?: string, body_snippet?: string, llm_inferred_event_date?: string|null }} row
 * @returns {string|null} YYYY-MM-DD
 */
export function resolveOfferDateForInboxRow(row) {
  const raw = row?.llm_inferred_event_date;
  if (raw != null && String(raw).trim() !== '') {
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  }
  return parseOfferEventDateFromInboxRow(row);
}

